import { useState, useEffect } from "react";
import useAccountEnhanced from "../../../hooks/use-account-enhanced";
import { Card } from "src/components/card";
import { WorksiteRepresentativeRaw } from "../types/WorksiteRepresentativeRaw";
import { WorksiteRepresentative } from "../types/WorksiteRepresentative";
import { HRAssignee } from "../types/HRAssignee";
import { WorksitePayload } from "../types/WorksitePayload";
import { CreateWorksitePayload } from "../types/CreateWorksitePayload";
import { WorksiteOrgRelationshipPayload } from "../types/WorksiteOrgRelationshipPayload";
import { OrgWorksiteRelationship } from "../types/OrgWorksiteRelationship";
import { Worksites } from "../types/Worksites";
import { useQuery } from "@tanstack/react-query";
import { hrisConfigMutations, hrisConfigQueryOptions } from "src/api/mvte/hris-rep-query-options";
import dayjs from "dayjs";

const emptyWorksiteForm: CreateWorksitePayload =
{
    Code: "",
    State: "",
    City: "",
    Street: "",
    Location: "",
    AddressOne: "",
    AddressTwo: "",
    ZipCode: "",
    ITOfficeLocationName: "",
    isActive: true,
};

type WorksiteOption =
{
    worksiteID: number;
    code: string;
};

function extractWorksiteId(result: unknown): number | null
{
    if (typeof result === "number") return result;
    if (result && typeof result === "object")
    {
        const id = (result as Record<string, unknown>).WorksiteID;
        if (id !== undefined && id !== null && !Number.isNaN(Number(id))) return Number(id);
    }
    return null;
}

function mapToRepresentative(raw: WorksiteRepresentativeRaw): WorksiteRepresentative
{
    return {
        eorName: raw.eorName,
        ccName: raw.ccName,
        WorksiteCode: raw.WorksiteCode,
        hrManager: raw.hrManager,
        hrPOCStaff: raw.hrPOCStaff,
        hrPOCDriver: raw.hrPOCDriver,
        isActive: raw.Active,
        ModifiedDateTime: raw.ModifiedDateTime,
        AssignedHRManager: raw.AssignedHRManager,
        AssignedHRPOCStaff: raw.AssignedHRPOCStaff,
        AssignedHRPOCDriver: raw.AssignedHRPOCDriver,
        OrgWorksiteID: raw.OrgWorksiteID,
    };
}
function ModifyWorksiteRepresentatives()
{
    const accountEnhanced = useAccountEnhanced();
    const [searchTerm, setSearchTerm] = useState("");
    const [columnFilters, setColumnFilters] = useState(
    {
        eorName: "",
        ccName: "",
        WorksiteCode: "",
        hrManager: "",
        hrPOCStaff: "",
        hrPOCDriver: "",
        ModifiedDateTime: ""
    });
    const [sortDir, setSortDir] = useState<true | false>(true)
    const [data, setData] = useState<WorksiteRepresentative[]>([]);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editValues, setEditValues] = useState<
    {
        hrManager: string;
        hrManagerID: number;
        hrPocStaff: string;
        hrPocStaffID: number;
        hrPocDriver: string;
        hrPocDriverID: number;
    } >({ hrManager: "", hrManagerID: 0, hrPocStaff: "", hrPocStaffID: 0, hrPocDriver: "", hrPocDriverID: 0 });

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [worksiteForm, setWorksiteForm] = useState<CreateWorksitePayload>(emptyWorksiteForm);
    const [createError, setCreateError] = useState<string | null>(null);

    const [showRelationshipForm, setShowRelationshipForm] = useState(false);
    const [newWorksite, setNewWorksite] = useState<WorksiteOption | null>(null);
    const [relationshipValues, setRelationshipValues] = useState(
    {
        worksiteID: 0,
        organizationRelationshipID: 0,
        hrManagerID: 0,
        hrPocStaffID: 0,
        hrPocDriverID: 0,
    });
    const [relationshipError, setRelationshipError] = useState<string | null>(null);

    const hrWorksiteRepQuery = useQuery({ ...hrisConfigQueryOptions.hrWorksiteRepQueryOptions() });
    const hrAssigneeQuery = useQuery({ ...hrisConfigQueryOptions.hrAssigneeQueryOptions() });
    const worksiteLookupQuery = useQuery({ ...hrisConfigQueryOptions.getWorksiteQueryOptions() });
    const orgRelationshipQuery = useQuery({ ...hrisConfigQueryOptions.getOrgWorksiteRelationshipQueryOptions() });
    const hrWorksiteMutation = hrisConfigMutations.useUpdateWorksiteHRAssignments();
    const createWorksiteMutation = hrisConfigMutations.useCreateWorksite();
    const createRelationshipMutation = hrisConfigMutations.useCreateOrgWorksiteRelationship();

    useEffect(() =>
    {
        if (hrWorksiteRepQuery.data)
        {
            setData(hrWorksiteRepQuery.data.map(mapToRepresentative));
        }
    }, [hrWorksiteRepQuery.data]);

    const assigneeOptions = (hrAssigneeQuery.data ?? []).map((a: HRAssignee) => (
    {
        EmployeeUID: a.EmployeeUID,
        EmployeeName: a.EmployeeName,
    }));

    const worksiteOptions: WorksiteOption[] = [
        ...(worksiteLookupQuery.data ?? []).map((w: Worksites) => ({ worksiteID: w.WorksiteID, code: w.WorksiteCode })),
        ...(newWorksite ? [newWorksite] : []),
    ].filter((opt, idx, arr) => arr.findIndex((o) => o.worksiteID === opt.worksiteID) === idx);

    const relationshipOptions = (orgRelationshipQuery.data ?? []).map((r: OrgWorksiteRelationship) => (
    {
        OrganizationRelationshipID: r.OrganizationRelationshipID,
        label: `${r.EmployerOfRecordName} - ${r.ClientCompanyName}`,
    }));

    const filteredData = data.filter((representatives) =>
    {
        const employerOfRecord = `${representatives.eorName}`.toLowerCase();
        const clientCompany = representatives.ccName.toLowerCase();
        const worksiteCode = representatives.WorksiteCode.toLowerCase();
        const hrManager = (representatives.hrManager ?? "").toLowerCase();
        const staffPOC = (representatives.hrPOCStaff ?? "").toLowerCase();
        const driverPOC = (representatives.hrPOCDriver ?? "").toLowerCase();
        const modifiedDate = dayjs(representatives.ModifiedDateTime).format("MM/D/YYYY h:mm A").toLowerCase();

        if (searchTerm.trim() !== "")
        {
            const term = searchTerm.toLowerCase();
            const matchesMain =
                employerOfRecord.includes(term) ||
                clientCompany.includes(term) ||
                worksiteCode.includes(term) ||
                hrManager.includes(term) ||
                staffPOC.includes(term) ||
                driverPOC.includes(term) ||
                modifiedDate.includes(term);
            if (!matchesMain) return false;
        }

        if (columnFilters.eorName.trim() !== "" && !employerOfRecord.includes(columnFilters.eorName.toLowerCase())) return false;
        if (columnFilters.ccName.trim() !== "" && !clientCompany.includes(columnFilters.ccName.toLowerCase())) return false;
        if (columnFilters.WorksiteCode.trim() !== "" && !worksiteCode.includes(columnFilters.WorksiteCode.toLowerCase())) return false;
        if (columnFilters.hrManager.trim() !== "" && !hrManager.includes(columnFilters.hrManager.toLowerCase())) return false;
        if (columnFilters.hrPOCStaff.trim() !== "" && !staffPOC.includes(columnFilters.hrPOCStaff.toLowerCase())) return false;
        if (columnFilters.hrPOCDriver.trim() !== "" && !driverPOC.includes(columnFilters.hrPOCDriver.toLowerCase())) return false;
        if (columnFilters.ModifiedDateTime.trim() !== "" && !modifiedDate.includes(columnFilters.ModifiedDateTime.toLowerCase())) return false;
        return true;
    })

    const sortData = sortDir === null ? filteredData : [...filteredData].sort((a,b) =>
    {
            const diff = dayjs(a.ModifiedDateTime).valueOf() - dayjs(b.ModifiedDateTime).valueOf();
            return sortDir ? diff : -diff;
    })

    function handleEdit(rep: WorksiteRepresentative)
    {
        setEditingId(rep.OrgWorksiteID);
        setEditValues(
        {
            hrManager: rep.hrManager,
            hrManagerID: rep.AssignedHRManager,
            hrPocStaff: rep.hrPOCStaff,
            hrPocStaffID: rep.AssignedHRPOCStaff,
            hrPocDriver: rep.hrPOCDriver,
            hrPocDriverID: rep.AssignedHRPOCDriver,
        });
    }

    async function handleConfirm()
    {
        if (editingId === null) return;

        const rep = data.find((r) => r.OrgWorksiteID === editingId);
        if (!rep) return;

        const noChanges =
            editValues.hrManagerID === rep.AssignedHRManager &&
            editValues.hrPocStaffID === rep.AssignedHRPOCStaff &&
            editValues.hrPocDriverID === rep.AssignedHRPOCDriver;
        if (noChanges)
        {
            setEditingId(null);
            return;
        }
        const newPayload: WorksitePayload =
        {
            orgWorksiteID: rep.OrgWorksiteID,
            pocManager: Number(editValues.hrManagerID),
            pocStaff: Number(editValues.hrPocStaffID),
            pocDriver: Number(editValues.hrPocDriverID),
            SourceSystem: "MVTE-Center",
            ChangedBy: accountEnhanced.me.employeeCode,
            Note: "Hardcoded note for now",
        };

        console.log("OrgWorksiteID on rep: ", rep.OrgWorksiteID, typeof rep.OrgWorksiteID)
        console.log("Full Payload: ", newPayload)
        await hrWorksiteMutation.mutateAsync({ payload: newPayload });

        setData((prev) =>
            prev.map((r) =>
            r.OrgWorksiteID === editingId
            ? {
                ...r,
                hrManager: editValues.hrManager,
                hrPOCStaff: editValues.hrPocStaff,
                hrPOCDriver: editValues.hrPocDriver,
                AssignedHRManager: editValues.hrManagerID,
                AssignedHRPOCStaff: editValues.hrPocStaffID,
                AssignedHRPOCDriver: editValues.hrPocDriverID,
            } : r )
        );
        setEditingId(null);
    }

    function handleCancel()
    {
        setEditingId(null);
    }

    function openCreateForm()
    {
        setWorksiteForm(emptyWorksiteForm);
        setCreateError(null);
        setShowCreateForm(true);
    }

    function closeCreateForm()
    {
        setShowCreateForm(false);
        setCreateError(null);
    }

    function updateWorksiteField<K extends keyof CreateWorksitePayload>(key: K, value: CreateWorksitePayload[K])
    {
        setWorksiteForm((prev) => ({ ...prev, [key]: value }));
    }

    async function handleCreateWorksite()
    {
        if (worksiteForm.Code.trim() === "")
        {
            setCreateError("Worksite code is required.");
            return;
        }

        const newPayload: CreateWorksitePayload = { ...worksiteForm };

        console.log("Create Worksite Payload: ", newPayload)
        try
        {
            const result = await createWorksiteMutation.mutateAsync({ payload: newPayload });
            const newWorksiteID = extractWorksiteId(result);

            setShowCreateForm(false);
            setCreateError(null);

            if (newWorksiteID !== null)
            {
                setNewWorksite({ worksiteID: newWorksiteID, code: newPayload.Code });
            }
            setRelationshipValues({
                worksiteID: newWorksiteID ?? 0,
                organizationRelationshipID: 0,
                hrManagerID: 0,
                hrPocStaffID: 0,
                hrPocDriverID: 0,
            });
            setRelationshipError(null);
            setShowRelationshipForm(true);

            worksiteLookupQuery.refetch();
            hrWorksiteRepQuery.refetch();
        }
        catch (err)
        {
            console.error("Failed to create worksite", err);
            setCreateError("Could not create the worksite. Please try again.");
        }
    }

    function openRelationshipForm(worksiteID: number = 0)
    {
        setRelationshipValues({ worksiteID, organizationRelationshipID: 0, hrManagerID: 0, hrPocStaffID: 0, hrPocDriverID: 0 });
        setRelationshipError(null);
        setShowRelationshipForm(true);
    }

    function closeRelationshipForm()
    {
        setShowRelationshipForm(false);
        setRelationshipError(null);
    }

    async function handleCreateRelationship()
    {
        if (relationshipValues.worksiteID === 0)
        {
            setRelationshipError("Select a worksite.");
            return;
        }
        if (relationshipValues.organizationRelationshipID === 0)
        {
            setRelationshipError("Select an organization relationship.");
            return;
        }
        if (
            relationshipValues.hrManagerID === 0 ||
            relationshipValues.hrPocStaffID === 0 ||
            relationshipValues.hrPocDriverID === 0
        )
        {
            setRelationshipError("Select an HR Manager, Staff POC, and Driver POC.");
            return;
        }

        const newPayload: WorksiteOrgRelationshipPayload =
        {
            OrganizationRelationshipID: Number(relationshipValues.organizationRelationshipID),
            WorksiteID: Number(relationshipValues.worksiteID),
            AssignedHRManager: Number(relationshipValues.hrManagerID),
            AssignedHRPOCStaff: Number(relationshipValues.hrPocStaffID),
            AssignedHRPOCDriver: Number(relationshipValues.hrPocDriverID),
        };

        console.log("Relationship Payload: ", newPayload)
        try
        {
            await createRelationshipMutation.mutateAsync({ payload: newPayload });
            setShowRelationshipForm(false);
            setRelationshipError(null);
            hrWorksiteRepQuery.refetch();
        }
        catch (err)
        {
            console.error("Failed to assign relationship", err);
            setRelationshipError("Could not assign the relationship. Please try again.");
        }
    }

    function renderAssigneeSelect(
        label: string,
        value: number,
        field: "hrManagerID" | "hrPocStaffID" | "hrPocDriverID")
    {
        return (
            <label style={fieldLabelStyle}>
                {label}
                <select
                    value={value}
                    onChange={(e) => setRelationshipValues((prev) => ({ ...prev, [field]: Number(e.target.value) }))}
                    style={selectStyle}>
                    <option value={0}>Select...</option>
                    {
                        assigneeOptions.map((opt) =>
                        (
                            <option key={opt.EmployeeUID} value={opt.EmployeeUID}>
                                {opt.EmployeeName}
                            </option>
                        ))
                    }
                </select>
            </label>
        );
    }

    function renderTextField(label: string, key: keyof CreateWorksitePayload)
    {
        return (
            <label style={fieldLabelStyle}>
                {label}
                <input
                    type="text"
                    value={worksiteForm[key] as string}
                    onChange={(e) => updateWorksiteField(key, e.target.value as never)}
                    style={formInputStyle}
                />
            </label>
        );
    }

    if (hrWorksiteRepQuery.isLoading)
    {
        return (
            <Card>
                <div style={{ padding: "24px", textAlign: "center", color: "#555", fontSize: "15px" }}>
                    Loading representatives...
                </div>
            </Card>
        );
    }
    return (
        <Card>
            <div style={{ padding: "24px" }}>
            {/* ── Toolbar ── */}
                <div style={toolbarStyle}>
                    <input
                        type="text"
                        placeholder="Search by value..."
                        value={searchTerm}
                        onChange= {(e) => setSearchTerm(e.target.value)}
                        style={searchInputStyle}/>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <button onClick={openCreateForm} style={editButtonStyle}>Create Worksite</button>
                        <button onClick={() => openRelationshipForm()} style={editButtonStyle}>Assign Relationship</button>
                    </div>
                </div>

            {/* ── Create Worksite form ── */}
                {showCreateForm && (
                    <div style={overlayStyle}>
                        <div style={modalStyle}>
                            <h3 style={modalTitleStyle}>Create worksite</h3>
                            <div style={formGridStyle}>
                                {renderTextField("Code", "Code")}
                                {renderTextField("IT Office Location Name", "ITOfficeLocationName")}
                                {renderTextField("Location", "Location")}
                                {renderTextField("Street", "Street")}
                                {renderTextField("Address One", "AddressOne")}
                                {renderTextField("Address Two", "AddressTwo")}
                                {renderTextField("City", "City")}
                                {renderTextField("State", "State")}
                                {renderTextField("Zip Code", "ZipCode")}
                                <label style={{ ...fieldLabelStyle, flexDirection: "row", alignItems: "center", gap: "8px" }}>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(worksiteForm.isActive)}
                                        onChange={(e) => updateWorksiteField("isActive", e.target.checked)}
                                    />
                                    Active
                                </label>
                            </div>
                            {createError && <div style={errorStyle}>{createError}</div>}
                            <div style={modalActionsStyle}>
                                <button
                                    onClick={handleCreateWorksite}
                                    disabled={createWorksiteMutation.isPending}
                                    style={confirmButtonStyle}>
                                    {createWorksiteMutation.isPending ? "Creating..." : "Create"}
                                </button>
                                <button onClick={closeCreateForm} style={cancelButtonStyle}>Cancel</button>
                            </div>
                        </div>
                    </div>
                )}

            {/* ── Assign Relationship form ── */}
                {showRelationshipForm && (
                    <div style={overlayStyle}>
                        <div style={modalStyle}>
                            <h3 style={modalTitleStyle}>Assign relationship</h3>
                            <div style={{ ...formGridStyle, gridTemplateColumns: "1fr" }}>
                                <label style={fieldLabelStyle}>
                                    Worksite
                                    <select
                                        value={relationshipValues.worksiteID}
                                        onChange={(e) => setRelationshipValues((prev) => ({ ...prev, worksiteID: Number(e.target.value) }))}
                                        style={selectStyle}>
                                        <option value={0}>Select...</option>
                                        {
                                            worksiteOptions.map((opt) =>
                                            (
                                                <option key={opt.worksiteID} value={opt.worksiteID}>
                                                    {opt.code}
                                                </option>
                                            ))
                                        }
                                    </select>
                                </label>
                                <label style={fieldLabelStyle}>
                                    Organization Relationship
                                    <select
                                        value={relationshipValues.organizationRelationshipID}
                                        onChange={(e) => setRelationshipValues((prev) => ({ ...prev, organizationRelationshipID: Number(e.target.value) }))}
                                        style={selectStyle}>
                                        <option value={0}>Select...</option>
                                        {
                                            relationshipOptions.map((opt) =>
                                            (
                                                <option key={opt.OrganizationRelationshipID} value={opt.OrganizationRelationshipID}>
                                                    {opt.label}
                                                </option>
                                            ))
                                        }
                                    </select>
                                </label>
                                {renderAssigneeSelect("HR Manager", relationshipValues.hrManagerID, "hrManagerID")}
                                {renderAssigneeSelect("Staff POC", relationshipValues.hrPocStaffID, "hrPocStaffID")}
                                {renderAssigneeSelect("Driver POC", relationshipValues.hrPocDriverID, "hrPocDriverID")}
                            </div>
                            {relationshipError && <div style={errorStyle}>{relationshipError}</div>}
                            <div style={modalActionsStyle}>
                                <button
                                    onClick={handleCreateRelationship}
                                    disabled={createRelationshipMutation.isPending}
                                    style={confirmButtonStyle}>
                                    {createRelationshipMutation.isPending ? "Saving..." : "Assign"}
                                </button>
                                <button onClick={closeRelationshipForm} style={cancelButtonStyle}>Cancel</button>
                            </div>
                        </div>
                    </div>
                )}

                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
                    <thead>
                        <tr style={{ backgroundColor: "#f0f0f0" }}>
                            <th style={thStyle}>Employer of Record</th>
                            <th style={thStyle}>Client Company</th>
                            <th style={thStyle}>Worksite Code</th>
                            <th style={thStyle}>HR Manager</th>
                            <th style={thStyle}>Staff POC</th>
                            <th style={thStyle}>Driver POC</th>
                            <th style={thStyle}>
                                Modified Date {"    "}
                                <button onClick={() => setSortDir(!sortDir)} title="Toggle ASC|DESC by Modified Date" style={sortButtonStyle}>
                                    {sortDir ? "▲" : "▼"}
                                </button>
                            </th>
                            <th style={thStyle}>Active</th>
                            <th style={{ ...thStyle, width: "160px" }}>Actions</th>
                        </tr>
                        <tr style={{ backgroundColor: "#f7f7f7" }}>
                            <th style={thStyle}>
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    value={columnFilters.eorName}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, eorName: e.target.value }))}
                                    style={columnSearchInputStyle}
                                />
                            </th>
                            <th style={thStyle}>
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    value={columnFilters.ccName}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, ccName: e.target.value }))}
                                    style={columnSearchInputStyle}
                                />
                            </th>
                            <th style={thStyle}>
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    value={columnFilters.WorksiteCode}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, WorksiteCode: e.target.value }))}
                                    style={columnSearchInputStyle}
                                />
                            </th>
                            <th style={thStyle}>
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    value={columnFilters.hrManager}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, hrManager: e.target.value }))}
                                    style={columnSearchInputStyle}
                                />
                            </th>
                            <th style={thStyle}>
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    value={columnFilters.hrPOCStaff}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, hrPOCStaff: e.target.value }))}
                                    style={columnSearchInputStyle}
                                />
                            </th>
                            <th style={thStyle}>
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    value={columnFilters.hrPOCDriver}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, hrPOCDriver: e.target.value }))}
                                    style={columnSearchInputStyle}
                                />
                            </th>
                            <th style={thStyle}>
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    value={columnFilters.ModifiedDateTime}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, ModifiedDateTime: e.target.value }))}
                                    style={columnSearchInputStyle}
                                />
                            </th>
                            <th style={thStyle}></th>
                            <th style={{ ...thStyle, width: "160px" }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortData.map((rep) => {
                            const isEditing = editingId === rep.OrgWorksiteID;
                            return (
                                <tr key={`${rep.OrgWorksiteID}`}>
                                    <td style={tdStyle}>{rep.eorName}</td>

                                    <td style={tdStyle}>{rep.ccName}</td>

                                    <td style={tdStyle}>{rep.WorksiteCode}</td>

                                    {/* HR Manager*/}
                                    <td style={tdStyle}>
                                    {
                                        isEditing ?
                                        (
                                            <select value={editValues.hrManagerID}
                                            onChange={(e) =>
                                            {
                                                const selected = assigneeOptions.find((o) => o.EmployeeUID === Number(e.target.value))!;
                                                setEditValues((prev) =>
                                                (
                                                    {
                                                        ...prev,
                                                        hrManager: selected.EmployeeName,
                                                        hrManagerID: selected.EmployeeUID,
                                                    }
                                                ));
                                            }}
                                            style={selectStyle}>
                                            {
                                                assigneeOptions.map((opt) =>
                                                (
                                                    <option key={opt.EmployeeUID} value={opt.EmployeeUID}>
                                                        {opt.EmployeeName}
                                                    </option>
                                                ))
                                            }
                                            </select>
                                        ) : (rep.hrManager)
                                    }
                                    </td>

                                    {/* Staff POC */}
                                    <td style={tdStyle}>
                                    {
                                        isEditing ?
                                        (
                                            <select value={editValues.hrPocStaffID}
                                            onChange={(e) =>
                                            {
                                                const selected = assigneeOptions.find((o) => o.EmployeeUID === Number(e.target.value))!;
                                                setEditValues((prev) =>
                                                (
                                                    {
                                                        ...prev,
                                                        hrPocStaff: selected.EmployeeName,
                                                        hrPocStaffID: selected.EmployeeUID,
                                                    }
                                                ));
                                            }}
                                            style={selectStyle}>
                                            {
                                                assigneeOptions.map((opt) =>
                                                (
                                                    <option key={opt.EmployeeUID} value={opt.EmployeeUID}>
                                                        {opt.EmployeeName}
                                                    </option>
                                                ))
                                            }
                                            </select>
                                        ) : (rep.hrPOCStaff)
                                    }
                                    </td>

                                    {/* Driver POC */}
                                    <td style={tdStyle}>
                                    {
                                        isEditing ?
                                        (
                                            <select value={editValues.hrPocDriverID}
                                            onChange={(e) =>
                                            {
                                                const selected = assigneeOptions.find((o) => o.EmployeeUID === Number(e.target.value))!;
                                                setEditValues((prev) =>
                                                ({
                                                    ...prev,
                                                    hrPocDriver: selected.EmployeeName,
                                                    hrPocDriverID: selected.EmployeeUID,
                                                }));
                                            }}
                                            style={selectStyle}>
                                            {
                                                assigneeOptions.map((opt) =>
                                                (
                                                    <option key={opt.EmployeeUID} value={opt.EmployeeUID}>
                                                        {opt.EmployeeName}
                                                    </option>
                                                ))
                                            }
                                            </select>) : (rep.hrPOCDriver)
                                    }
                                    </td>

                                    <td style={tdStyle}>
                                        <span title={rep.ModifiedDateTime} style={{ cursor: "default" }}>
                                            {dayjs(rep.ModifiedDateTime).format("MM/D/YYYY h:mm A")}
                                        </span>
                                    </td>

                                    <td style={tdStyle}>{rep.isActive ? "Yes" : "No"}</td>

                                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                                        {
                                            isEditing ?
                                            (
                                                <>
                                                    <button onClick={handleConfirm} style={confirmButtonStyle}>Confirm</button>
                                                    <button onClick={handleCancel} style={cancelButtonStyle}>Cancel</button>
                                                </>
                                            ) : ( <button onClick={() => handleEdit(rep)} style={editButtonStyle}>Edit</button>)
                                        }
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}


const thStyle: React.CSSProperties =
{
    border: "1px solid #ccc",
    padding: "10px 12px",
    textAlign: "center",
    fontWeight: 600,
};
const tdStyle: React.CSSProperties =
{
    border: "1px solid #ccc",
    padding: "10px 12px",
    textAlign: "center",
    fontSize: "14px",
};
const selectStyle: React.CSSProperties =
{
    width: "100%",
    padding: "4px 6px",
    fontSize: "14px",
    border: "1px solid #aaa",
    borderRadius: "4px",
};
const columnSearchInputStyle: React.CSSProperties =
{
    width: "100%",
    padding: "4px 6px",
    fontSize: "12px",
    fontWeight: 400,
    border: "1px solid #ccc",
    borderRadius: "4px",
    outline: "none",
    boxSizing: "border-box",
    color: "#222",
};
const editButtonStyle: React.CSSProperties =
{
    padding: "4px 12px",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#0078d4",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
};
const confirmButtonStyle: React.CSSProperties =
{
    padding: "4px 10px",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#2e7d32",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    marginRight: "6px",
};
const cancelButtonStyle: React.CSSProperties =
{
    padding: "4px 10px",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#999",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
};
const toolbarStyle: React.CSSProperties =
{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
};
const searchInputStyle: React.CSSProperties =
{
    flex: 1,
    maxWidth: "320px",
    padding: "6px 10px",
    fontSize: "13px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    outline: "none",
    color: "#222",
}
const sortButtonStyle: React.CSSProperties = 
{
    padding: "0.6px",
    fontSize: "12px",
    cursor: "pointer",
    backgroundColor: "transparent",
    border: "1px solid #aaa",
    borderRadius: "4px",
    lineHeight: "1.6",
}
const overlayStyle: React.CSSProperties =
{
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
}
const modalStyle: React.CSSProperties =
{
    backgroundColor: "#fff",
    borderRadius: "6px",
    padding: "24px",
    width: "560px",
    maxWidth: "92vw",
    maxHeight: "90vh",
    overflowY: "auto",
}
const modalTitleStyle: React.CSSProperties =
{
    margin: "0 0 16px 0",
    fontSize: "18px",
    fontWeight: 600,
}
const modalActionsStyle: React.CSSProperties =
{
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "20px",
}
const formGridStyle: React.CSSProperties =
{
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
}
const fieldLabelStyle: React.CSSProperties =
{
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#333",
}
const formInputStyle: React.CSSProperties =
{
    padding: "6px 8px",
    fontSize: "14px",
    fontWeight: 400,
    border: "1px solid #aaa",
    borderRadius: "4px",
    boxSizing: "border-box",
    width: "100%",
}
const errorStyle: React.CSSProperties =
{
    marginTop: "12px",
    color: "#b00020",
    fontSize: "13px",
}
export { ModifyWorksiteRepresentatives };